import { type FC } from "react";

import { Check, Clock, TriangleAlert, Zap } from "lucide-react";
import { Link } from "react-router-dom";

import { Tag, type TagColor } from "@/shared/components/ui/tag";
import { CATEGORY_BG, MODULE_IMAGES } from "@/shared/constants/estateEssentials";
import { cn } from "@/shared/lib/utils";
import { trackEvent, EventType } from "@/shared/utils/core";

import type { EssentialModule, EssentialTag } from "@/shared/types/estateEssentials";

const TAG_CONFIG: Record<EssentialTag, { label: string; icon: typeof Clock; color: TagColor }> = {
  "time-sensitive": {
    label: "Time sensitive",
    icon: TriangleAlert,
    color: "salmon",
  },
  "save-time": {
    label: "Save 1 hour",
    icon: Clock,
    color: "stone",
  },
  "elayne-can-do": {
    label: "Elayne can do this for you",
    icon: Zap,
    color: "blue",
  },
};

interface ModuleCardProps {
  module: EssentialModule;
  firstName?: string;
  isComplete?: boolean;
  onClickLocked?: () => void;
  className?: string;
}

const DEFAULT_WIDTH =
  "w-[75vw] sm:w-[calc((100%-16px)/1.4)] md:w-[calc((100%-32px)/2.5)] lg:w-[calc((100%-48px)/3.2)] xl:w-[calc((100%-48px)/3.5)]";

export const ModuleCard: FC<ModuleCardProps> = ({
  module,
  firstName,
  isComplete,
  onClickLocked,
  className,
}) => {
  const bgColor = CATEGORY_BG[module.category];
  const illustration = MODULE_IMAGES[module.id];

  const displayName =
    module.nameTemplate && firstName
      ? module.nameTemplate.replace("{{ firstName }}", firstName)
      : module.name;

  return (
    <Link
      to={`/important-todos/${module.id}`}
      onClick={(e) => {
        trackEvent(EventType.ESSENTIALS_MODULE_CLICKED, {
          module_id: module.id,
          category: module.category,
          is_complete: !!isComplete,
          is_locked: !!onClickLocked,
        });
        if (onClickLocked) {
          e.preventDefault();
          onClickLocked();
        }
      }}
      className={cn(
        "flex flex-col shrink-0 rounded-lg border border-primary bg-white overflow-hidden hover:border-strong transition-colors no-underline",
        className ?? DEFAULT_WIDTH,
      )}
    >
      <div
        className={cn(
          "relative flex items-center justify-center h-[150px] overflow-hidden",
          bgColor,
        )}
      >
        {isComplete && (
          <>
            <div className="absolute inset-0 bg-white/50 z-[1]" />
            <Tag color="brand" size="sm" icon={Check} className="absolute top-3 right-3 z-[2]">
              Complete
            </Tag>
          </>
        )}
        <img src={illustration} alt="" className="size-[130px] object-contain" />
      </div>

      <div className="flex flex-col flex-1 justify-between p-4">
        <div>
          <p className="text-sm font-semibold">{displayName}</p>
          {module.description && (
            <p className="text-[13px] text-secondary leading-normal mt-1">
              {firstName
                ? module.description
                    .replace("[First name]", firstName)
                    .replace("{{ firstName }}", firstName)
                : module.description}
            </p>
          )}
        </div>

        {module.tags && module.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 pt-4">
            {module.tags.map((tag) => {
              const config = TAG_CONFIG[tag];
              return (
                <Tag key={tag} color={config.color} size="sm" icon={config.icon} iconStroke={2}>
                  {config.label}
                </Tag>
              );
            })}
          </div>
        )}
      </div>
    </Link>
  );
};
